import i18next from 'i18next';
import React from 'react';
import classNames from 'classnames';
import Logger from '../lib/Logger';
import { VIDEO_CONSTRAINTS, VIDEO_CODECS } from '../lib/RoomClient';
import '../styles/MediaSettings.scss';

const log = new Logger('MediaSettings');
log.debug('MediaSettings');

interface Device {
    id: string;
    label: string;
}

type Props = {
    webcams: Device[];
    mics: Device[];
    videoOn: boolean;
    videoSource: 'webcam' | 'screen';
    activeWebcamId: string | null;
    micOn: boolean;
    activeMicId: string | null;
    activeResolution: string;
    activeCodec: string;
    onChooseWebcam: (id: string) => void;
    onShareScreen: () => void;
    onDisableWebcam: () => void;
    onChooseMic: (id: string) => void;
    onDisableMic: () => void;
    onChooseResolution: (key: string) => void;
    onChooseCodec: (key: string) => void;
    onClose: () => void;
}

export default function MediaSettings(props: Props) {
    const {
        webcams,
        mics,
        videoOn,
        videoSource,
        activeWebcamId,
        micOn,
        activeMicId,
        activeResolution,
        activeCodec,
        onChooseWebcam,
        onShareScreen,
        onDisableWebcam,
        onChooseMic,
        onDisableMic,
        onChooseResolution,
        onChooseCodec,
        onClose
    } = props;

    const resolutions = Array.from(VIDEO_CONSTRAINTS.keys());
    const codecs = Array.from(VIDEO_CODECS.keys());

    return (
        <div className='media-settings-backdrop' onClick={e => { e.stopPropagation(); onClose(); }}>
            <div data-component='MediaSettings' onClick={e => e.stopPropagation()}>
                <div className='cancel' onClick={onClose} />
                <div className='title'>{i18next.t('media.settings')}</div>

                <div className='section'>
                    <div className='header'>{i18next.t('media.choose-webcam')}</div>
                    <ul>
                        {webcams.map(webcam => (
                            <li
                                key={webcam.id}
                                className={classNames('webcam', {'webcam-active': videoOn && videoSource === 'webcam' && activeWebcamId === webcam.id})}
                                onClick={() => onChooseWebcam(webcam.id)}
                            >{webcam.label}</li>
                        ))}
                        <li
                            className={classNames('screen-share', {'screen-share-active': videoOn && videoSource === 'screen'})}
                            onClick={onShareScreen}
                        >{i18next.t('media.share-screen')}</li>
                        <li
                            className={classNames('webcam-disable', {'webcam-active': !videoOn})}
                            onClick={onDisableWebcam}
                        >{i18next.t('media.disable')}</li>
                    </ul>
                </div>

                <div className='section'>
                    <div className='header'>{i18next.t('media.choose-mic')}</div>
                    <ul>
                        {mics.map(mic => (
                            <li
                                key={mic.id}
                                className={classNames('mic', {'mic-active': micOn && activeMicId === mic.id})}
                                onClick={() => onChooseMic(mic.id)}
                            >{mic.label}</li>
                        ))}
                        <li
                            className={classNames('mic-disable', {'mic-active': !micOn})}
                            onClick={onDisableMic}
                        >{i18next.t('media.disable')}</li>
                    </ul>
                </div>

                <div className='section'>
                    <div className='header'>{i18next.t('media.resolution')}</div>
                    <ul>
                        {resolutions.map(key => (
                            <li
                                key={key}
                                className={classNames('resolution', {'resolution-active': activeResolution === key})}
                                onClick={() => onChooseResolution(key)}
                            >{i18next.t(`media.resolutions.${key}`)}</li>
                        ))}
                    </ul>
                </div>

                <div className='section'>
                    <div className='header'>{i18next.t('media.quality')}</div>
                    <ul>
                        {codecs.map(key => (
                            <li
                                key={key}
                                className={classNames('resolution', {'resolution-active': activeCodec === key})}
                                onClick={() => onChooseCodec(key)}
                            >{i18next.t(`media.qualities.${key}`)}</li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    )
}
